"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { SectionBody, SectionCard } from "../section-layout";
import { parseBulletBlocks } from "@/features/candidate/lib/profile-utils";
import { HighlightedText } from "@/features/arbitrator/components/review/highlighted-text";
import { useCitationContext } from "@/features/arbitrator/components/review/citation-context";

const COLLAPSED_MAX_CHARS = 320;

function renderBlocks(blocks: ReturnType<typeof parseBulletBlocks>): ReactNode[] {
  return blocks.map((b, i) => {
    if (b.type === "list" && b.items?.length) {
      return (
        <ul
          key={i}
          className="list-disc space-y-1 pl-5 marker:text-foreground/45"
        >
          {b.items.map((item, j) => (
            <li key={j} className="pl-0.5">
              <HighlightedText source="summary">{item}</HighlightedText>
            </li>
          ))}
        </ul>
      );
    }
    if (b.type === "paragraph" && b.text) {
      return (
        <p key={i} className="whitespace-pre-wrap">
          <HighlightedText source="summary">{b.text}</HighlightedText>
        </p>
      );
    }
    return null;
  });
}

export function AboutSection({ summary }: { summary: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const citationCtx = useCitationContext();
  const hasSummaryCitation = (citationCtx?.citations ?? []).some(
    (c) => c.source === "summary",
  );

  useEffect(() => {
    if (hasSummaryCitation && !expanded) setExpanded(true);
  }, [hasSummaryCitation]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsTruncate = !!summary && summary.length > COLLAPSED_MAX_CHARS;
  const displayText = useMemo(() => {
    if (!summary) return "";
    if (expanded || !needsTruncate) return summary;
    return summary.slice(0, COLLAPSED_MAX_CHARS).trimEnd() + "…";
  }, [summary, expanded, needsTruncate]);

  const blocks = useMemo(() => parseBulletBlocks(displayText), [displayText]);

  if (!summary) return null;

  return (
    <SectionCard title="About">
      <SectionBody>
        <div
          data-evidence-source="summary"
          className="flex w-full flex-col gap-2 text-sm font-normal leading-normal text-foreground/70 tracking-[-0.14px]"
        >
          {renderBlocks(blocks)}
        </div>
        {needsTruncate && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 py-2 text-sm font-medium text-primary hover:underline self-start sm:py-0"
          >
            {expanded ? "See less" : "See more"}
          </button>
        )}
      </SectionBody>
    </SectionCard>
  );
}
